/**
 * Plans v2 card 3.3: the plan's local caps next to what the user has, for
 * PlanLimitsPanel and the create buttons. One shared count (a small store),
 * however many buttons show it: refreshed when the plan, the open workspace's
 * collections / environments / workspaces or the vault list change, and once
 * a minute while a plan with caps is active (boards, documents and events
 * live in the records DB, which has no store to watch). Concurrent refreshes
 * coalesce into one.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { useEntitlementsStore } from '../../store/entitlementsStore';
import { useCollectionsStore } from '../../store/collectionsStore';
import { useVariablesStore } from '../../store/variablesStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useVaultStore } from '../../store/vaultStore';
import { localCapRows } from '../../plans/localUsage';

export const LOCAL_CAPS_REFRESH_MS = 60 * 1000;

export const useLocalCapsStore = create(() => ({ rows: [] }));

let inFlight = null;
let again = false;

/** Count every capped kind again (coalesced). Resolves with the rows. */
export function refreshLocalCaps() {
  if (inFlight) {
    again = true;
    return inFlight;
  }
  inFlight = (async () => {
    let rows;
    do {
      again = false;
      // eslint-disable-next-line no-await-in-loop
      rows = await localCapRows();
    } while (again);
    useLocalCapsStore.setState({ rows });
    return rows;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** `[{ kind, used, limit, full, text }]` for every capped kind (empty on a paid plan). */
export function useLocalCapRows() {
  const limits = useEntitlementsStore((s) => s.limits);
  const collections = useCollectionsStore((s) => s.collections);
  const environments = useVariablesStore((s) => s.environments);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const vaultItems = useVaultStore((s) => s.items);
  const rows = useLocalCapsStore((s) => s.rows);

  const capped = Object.values(limits || {}).some((v) => v !== null && v !== undefined);

  useEffect(() => {
    if (!capped) {
      if (useLocalCapsStore.getState().rows.length) useLocalCapsStore.setState({ rows: [] });
      return;
    }
    refreshLocalCaps();
  }, [capped, limits, collections, environments, workspaces, vaultItems]);

  useEffect(() => {
    if (!capped) return undefined;
    const timer = setInterval(() => refreshLocalCaps(), LOCAL_CAPS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [capped]);

  return capped ? rows : [];
}

/** The row for one kind, or null (uncapped, or not countable right now). */
export function useLocalCapRow(kind) {
  return useLocalCapRows().find((r) => r.kind === kind) || null;
}
